import { useEffect, useMemo, useState } from 'react';
import { Magnet as MagnetIcon, Eye, Download, Send, Heart, Activity, BarChart3, Plug } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { useRoleInfo } from '@/hooks/usePermissions';
import magnetService from '@/services/api/magnetService';
import EngagementTable from './EngagementTable';
import EventTimelineDrawer from './EventTimelineDrawer';
import ProvisionPanel from './ProvisionPanel';
import {
  getSummary, getEngagement, getEvents,
  type PortalSummary, type EngagementRow, type TrackingEventRow,
} from '../services/magnetPortalService';

type Tab = 'overview' | 'engagement' | 'connect';
const STATUS_FILTERS = ['any', 'opened', 'filled', 'submitted', 'downloaded', 'liked'] as const;

function countOf(summary: PortalSummary | null, ...types: string[]) {
  if (!summary) return 0;
  return summary.eventsByType
    .filter((e) => types.includes(e.event))
    .reduce((a, b) => a + b.count, 0);
}

function KpiCard({ icon: Icon, label, value }: { icon: any; label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-muted-foreground text-xs mb-2">
        <Icon className="h-4 w-4" />{label}
      </div>
      <div className="text-2xl font-semibold text-foreground">{value.toLocaleString()}</div>
    </div>
  );
}

export default function MagnetPortalPage() {
  const { isAdmin, isABMManager } = useRoleInfo();
  const canConnect = isAdmin || isABMManager;

  const [tab, setTab] = useState<Tab>('overview');
  const [summary, setSummary] = useState<PortalSummary | null>(null);
  const [recent, setRecent] = useState<TrackingEventRow[]>([]);

  const [rows, setRows] = useState<EngagementRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<EngagementRow | null>(null);

  const [magnets, setMagnets] = useState<{ id: string; title: string }[]>([]);
  const [magnetId, setMagnetId] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]>('any');

  // Overview data
  useEffect(() => {
    getSummary().then(setSummary).catch(() => {});
    getEvents({ take: 10 }).then((r) => setRecent(r.data)).catch(() => {});
    magnetService.getAllMagnets()
      .then((res) => setMagnets(res.data.map((m) => ({ id: m.id, title: m.title }))))
      .catch(() => {});
  }, []);

  // Engagement data (server-side magnet filter)
  useEffect(() => {
    setLoading(true);
    getEngagement({ magnetId: magnetId || undefined, take: 200 })
      .then((r) => { setRows(r.data); setTotal(r.total); })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [magnetId]);

  // Client-side search + status filter
  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== 'any' && !r.status[statusFilter]) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${r.contact.firstName} ${r.contact.lastName} ${r.contact.email || ''} ${r.magnet.title} ${r.contact.account?.name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, statusFilter]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Magnet Portal"
        description="Lead-magnet engagement from resources.cantier.com, synced to ABM contacts"
        icon={MagnetIcon}
      />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {([
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'engagement', label: 'Engagement', icon: Activity },
          ...(canConnect ? [{ id: 'connect', label: 'Connect', icon: Plug }] : []),
        ] as { id: Tab; label: string; icon: any }[]).map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <KpiCard icon={Activity} label="Total events" value={countOf(summary, ...(summary?.eventsByType.map((e) => e.event) || []))} />
            <KpiCard icon={Eye} label="Views" value={countOf(summary, 'VIEW', 'OPEN')} />
            <KpiCard icon={Download} label="Downloads" value={countOf(summary, 'DOWNLOAD')} />
            <KpiCard icon={Send} label="Submissions" value={countOf(summary, 'FORM_SUBMIT', 'ASSESSMENT_COMPLETE')} />
            <KpiCard icon={Heart} label="Likes" value={countOf(summary, 'LIKE')} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="text-sm font-semibold mb-4">Top magnets</h3>
              {!summary?.topMagnets.length ? (
                <p className="text-sm text-muted-foreground">No engagement recorded yet.</p>
              ) : (
                <ul className="space-y-2">
                  {summary.topMagnets.map((m) => (
                    <li key={m.magnet.id} className="flex items-center justify-between text-sm">
                      <span className="text-foreground truncate">{m.magnet.title || m.magnet.id}</span>
                      <span className="text-xs text-muted-foreground shrink-0 ml-3">
                        {m.contactsReached} contacts · {m.totalEvents} events
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="text-sm font-semibold mb-4">Recent activity</h3>
              {!recent.length ? (
                <p className="text-sm text-muted-foreground">Nothing yet.</p>
              ) : (
                <ul className="space-y-2.5">
                  {recent.map((e) => (
                    <li key={e.id} className="flex items-center justify-between text-sm">
                      <span className="text-foreground truncate">
                        {e.contact ? `${e.contact.firstName} ${e.contact.lastName}` : 'Someone'}
                        <span className="text-muted-foreground"> · {e.eventType.replace(/_/g, ' ').toLowerCase()}</span>
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0 ml-3">
                        {new Date(e.createdAt).toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Engagement */}
      {tab === 'engagement' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search contact, email, account, magnet…"
              className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
            <select
              value={magnetId} onChange={(e) => setMagnetId(e.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="">All magnets</option>
              {magnets.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
            </select>
            <select
              value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm capitalize"
            >
              {STATUS_FILTERS.map((s) => <option key={s} value={s}>{s === 'any' ? 'Any status' : s}</option>)}
            </select>
          </div>

          <div className="rounded-xl border border-border bg-card">
            <div className="px-4 py-2.5 text-xs text-muted-foreground border-b border-border">
              {loading ? 'Loading…' : `${filtered.length} of ${total} engagements`}
            </div>
            <EngagementTable rows={filtered} loading={loading} onSelect={setSelected} />
          </div>
        </div>
      )}

      {/* Connect */}
      {tab === 'connect' && canConnect && <ProvisionPanel />}

      <EventTimelineDrawer row={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
